import { lazy, Suspense, useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HomePage } from '@components/home';
import { CookieConsentBanner, PrivacyPolicy } from '@components/shared';
import { loadTranslation, supportedLanguages } from '@/i18n';
import { trackPageView } from '@utils/analytics';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import './styles/main.scss';

// Lazy-loaded route components
const SupportIndex = lazy(() =>
  import('@components/support/SupportIndex').then((m) => ({ default: m.SupportIndex }))
);
const WindowsSupportPage = lazy(() =>
  import('@components/support/pages').then((m) => ({ default: m.WindowsSupportPage }))
);
const MacOSSupportPage = lazy(() =>
  import('@components/support/pages').then((m) => ({ default: m.MacOSSupportPage }))
);
const LinuxSupportPage = lazy(() =>
  import('@components/support/pages').then((m) => ({ default: m.LinuxSupportPage }))
);
const ReleasesIndex = lazy(() =>
  import('@components/releases/ReleasesIndex').then((m) => ({ default: m.ReleasesIndex }))
);
const Emoji170ReleasePage = lazy(() =>
  import('@components/releases/pages').then((m) => ({ default: m.Emoji170ReleasePage }))
);
const Emoji16ReleasePage = lazy(() =>
  import('@components/releases/pages').then((m) => ({ default: m.Emoji16ReleasePage }))
);
const Emoji151ReleasePage = lazy(() =>
  import('@components/releases/pages').then((m) => ({ default: m.Emoji151ReleasePage }))
);
const Emoji150ReleasePage = lazy(() =>
  import('@components/releases/pages').then((m) => ({ default: m.Emoji150ReleasePage }))
);
const Emoji140ReleasePage = lazy(() =>
  import('@components/releases/pages').then((m) => ({ default: m.Emoji140ReleasePage }))
);
const Emoji131ReleasePage = lazy(() =>
  import('@components/releases/pages').then((m) => ({ default: m.Emoji131ReleasePage }))
);
const Emoji130ReleasePage = lazy(() =>
  import('@components/releases/pages').then((m) => ({ default: m.Emoji130ReleasePage }))
);
// Direct-path import (no barrel edit — src/components/shared/index.ts is in
// neither dev's grant this plan); lazy like every other routed page.
const NotFoundPage = lazy(() =>
  import('@components/shared/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);

const App = () => {
  const { i18n } = useTranslation();
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Track SPA page views on route changes
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  // Sync URL language parameter only when the user explicitly changes language.
  // Do NOT auto-add ?lang= on mount — this confuses Googlebot (it sees a URL
  // change from the canonical and may flag it as a redirect).
  //
  // Deps are intentionally [i18n.language] ONLY: this effect must not re-run on
  // location changes, or it would rewrite the URL back on Back/Forward before
  // the URL→language effect below has synced the language (avoids a
  // sync loop). location/navigate are read fresh from the render that the
  // language change triggers.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlLang = searchParams.get('lang');

    // Only update if the URL already has a lang param that differs
    if (urlLang && urlLang !== i18n.language) {
      searchParams.set('lang', i18n.language);
      navigate(
        { pathname: location.pathname, search: `?${searchParams.toString()}`, hash: location.hash },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // URL → language: when the URL carries a present and VALID ?lang= that
  // differs from the active language (e.g. Back/Forward navigation), load the
  // bundle and switch. An ABSENT param does nothing — a bare URL must never
  // force a language change or gain a ?lang= param (GSC pitfall #1).
  useEffect(() => {
    const urlLang = new URLSearchParams(location.search).get('lang');
    if (!urlLang || !supportedLanguages.includes(urlLang) || urlLang === i18n.language) {
      return;
    }

    let cancelled = false;
    (async () => {
      await loadTranslation(urlLang);
      if (!cancelled) {
        i18n.changeLanguage(urlLang);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search, i18n]);

  // Handle #privacy hash in URL
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#privacy') {
        setPrivacyOpen(true);
      }
    };

    // Check on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handlePrivacyClose = () => {
    setPrivacyOpen(false);
    // Remove hash from URL
    if (window.location.hash === '#privacy') {
      window.history.pushState(
        '',
        document.title,
        window.location.pathname + window.location.search
      );
    }
  };

  return (
    <>
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '60vh',
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/support" element={<SupportIndex />} />
          <Route path="/support/windows" element={<WindowsSupportPage />} />
          <Route path="/support/macos" element={<MacOSSupportPage />} />
          <Route path="/support/linux" element={<LinuxSupportPage />} />
          <Route path="/releases" element={<ReleasesIndex />} />
          <Route path="/releases/emoji-17" element={<Emoji170ReleasePage />} />
          <Route path="/releases/emoji-16" element={<Emoji16ReleasePage />} />
          <Route path="/releases/emoji-15-1" element={<Emoji151ReleasePage />} />
          <Route path="/releases/emoji-15" element={<Emoji150ReleasePage />} />
          <Route path="/releases/emoji-14" element={<Emoji140ReleasePage />} />
          <Route path="/releases/emoji-13-1" element={<Emoji131ReleasePage />} />
          <Route path="/releases/emoji-13" element={<Emoji130ReleasePage />} />
          {/* Soft-404 catch-all: NotFoundPage flips the robots meta to noindex
              while mounted (see the component for the update-in-place rule). */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>

      {/* Cookie Consent Banner for GDPR/CCPA Compliance */}
      <CookieConsentBanner />

      {/* Privacy Policy Dialog */}
      <PrivacyPolicy open={privacyOpen} onClose={handlePrivacyClose} />
    </>
  );
};

export default App;
